// InvoiceEmailDialog — диалог «Отправить письмо» на счёте (V15 «Почта» W7, ТЗ §3.8).
// Селект шаблона (свои + системный read-only) и аккаунта (дефолт — активный activeAccountId),
// Превью POST /api/email/templates/preview (subject/body — ТЕКСТ: textContent, без
// dangerouslySetInnerHTML — ТЗ §3.8/§11, XSS-поверхность), Отправить → POST
// /api/invoices/{id}/send-email → 202 {deliveryId, status, error?} (M-3): SENT →
// «Письмо отправлено»; FAILED → «Письмо не отправлено: {error}» (не 5xx).
// Нет аккаунтов → disabled + «Настройте почту…»; нет шаблонов → «Шаблонов писем нет…».

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { emailApi } from '@/api/email';
import { invoicesApi, type Invoice } from '@/api/invoices';
import { extractApiErrorMessage } from '@/lib/apiError';
import { t } from '@/i18n';

/** Справочники шаблонов/аккаунтов в диалоге (нормально помещаются; размер ≤100). */
const OPTIONS_PAGE_SIZE = 100;

interface InvoiceEmailDialogProps {
  open: boolean;
  onClose: () => void;
  /** Счёт, по которому отправляется письмо (только OPEN — кнопка скрыта для остальных, §5 п.4). */
  invoice: Invoice;
}

/** Диалог «Отправить письмо»: шаблон + аккаунт + превью + отправка (202-контракт M-3). */
export function InvoiceEmailDialog({ open, onClose, invoice }: InvoiceEmailDialogProps): JSX.Element {
  const queryClient = useQueryClient();
  const [templateId, setTemplateId] = useState<string | null>(null);
  const [accountId, setAccountId] = useState<string | null>(null);
  const [preview, setPreview] = useState<{ subject: string; body: string } | null>(null);

  const templatesQuery = useQuery({
    queryKey: ['email', 'templates'],
    queryFn: () => emailApi.listTemplates(0, OPTIONS_PAGE_SIZE),
    enabled: open,
    retry: false,
  });
  const settingsQuery = useQuery({
    queryKey: ['email', 'settings'],
    queryFn: () => emailApi.getSettings(),
    enabled: open,
    retry: false,
  });

  const templates = templatesQuery.data?.content ?? [];
  const accounts = settingsQuery.data?.accounts ?? [];
  // Дефолт аккаунта — активный (§5 п.5: activeAccountId); откат — первый в списке.
  const defaultAccountId = settingsQuery.data?.activeAccountId ?? accounts[0]?.id ?? null;
  // Дефолт шаблона — первый из списка (системный «Счёт» первым — is_system DESC, BE §3.5).
  const selectedTemplateId = templateId ?? templates[0]?.id ?? null;
  const selectedAccountId = accountId ?? defaultAccountId;

  const previewMutation = useMutation({
    mutationFn: () => {
      if (!selectedTemplateId) throw new Error('Выберите шаблон');
      return emailApi.previewTemplate({ templateId: selectedTemplateId, invoiceId: invoice.id });
    },
    onSuccess: (data) => setPreview({ subject: data.subject, body: data.body }),
  });

  const sendMutation = useMutation({
    mutationFn: () => {
      if (!selectedTemplateId) throw new Error('Выберите шаблон');
      // Без явного выбора — активный/первый аккаунт (BE §5 п.5: defaults).
      return invoicesApi.sendEmail(invoice.id, {
        templateId: selectedTemplateId,
        ...(selectedAccountId ? { accountId: selectedAccountId } : {}),
      });
    },
    onSuccess: (response) => {
      void queryClient.invalidateQueries({ queryKey: ['email'] });
      // SENT/FAILED — результат в диалоге (M-3: исход в теле 202); закрытие — пользователем.
    },
  });

  const isPending = sendMutation.isPending || previewMutation.isPending;
  const sendError = sendMutation.isError ? (
    <Alert severity="error">{extractApiErrorMessage(sendMutation.error, t('email.send.error'))}</Alert>
  ) : null;
  const previewError = previewMutation.isError ? (
    <Alert severity="error">{extractApiErrorMessage(previewMutation.error, t('email.templates.previewError'))}</Alert>
  ) : null;
  const sendResult = sendMutation.data && sendMutation.data.status === 'FAILED' ? (
    <Alert severity="error">
      {t('email.send.failed', { error: sendMutation.data.error ?? t('email.send.error') })}
    </Alert>
  ) : sendMutation.data && sendMutation.data.status === 'SENT' ? (
    <Alert severity="success">{t('email.send.success')}</Alert>
  ) : null;

  const noAccounts = !settingsQuery.isLoading && accounts.length === 0;
  const noTemplates = !templatesQuery.isLoading && templates.length === 0;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{t('email.send.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {sendError}
          {sendResult}
          <TextField
            select
            label={t('email.send.template')}
            value={selectedTemplateId ?? ''}
            onChange={(e) => {
              setTemplateId(e.target.value || null);
              setPreview(null);
            }}
            inputProps={{ 'aria-label': t('email.send.template') }}
          >
            {templates.map((item) => (
              <MenuItem key={item.id} value={item.id}>
                {item.name}
              </MenuItem>
            ))}
          </TextField>

          {noAccounts ? (
            <Alert severity="warning">{t('email.send.notConfigured')}</Alert>
          ) : (
            <TextField
              select
              label={t('email.send.account')}
              value={selectedAccountId ?? ''}
              onChange={(e) => {
                setAccountId(e.target.value || null);
              }}
              inputProps={{ 'aria-label': t('email.send.account') }}
            >
              {accounts.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.fromEmail}
                </MenuItem>
              ))}
            </TextField>
          )}

          <Stack direction="row" spacing={2} alignItems="center">
            <Button
              variant="outlined"
              onClick={() => previewMutation.mutate()}
              disabled={isPending || !selectedTemplateId || noAccounts}
            >
              {t('email.send.preview')}
            </Button>
          </Stack>

          {previewError}
          {preview ? (
            <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
              <Typography variant="subtitle1">{preview.subject}</Typography>
              {/* textContent-рендер: HTML-теги отображаются как текст (канон U-33/§3.8). */}
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {preview.body}
              </Typography>
            </Box>
          ) : null}

          {noTemplates ? <Alert severity="warning">{t('email.send.noTemplates')}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button
          variant="contained"
          onClick={() => sendMutation.mutate()}
          disabled={isPending || sendMutation.isSuccess || !selectedTemplateId || noAccounts || noTemplates}
        >
          {t('email.send.submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
